/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { ComponentChildren, ComponentProps, Ref, JSX } from 'preact';
import { useMemo } from 'preact/hooks';
import { forwardRef } from 'preact/compat';

import { BaseRichSelection } from '#PRIVATE_RichSelectionCommon';
import type { FocusableHandle } from '#hooks/UNSAFE_useFocusableTextField';
import type { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import { CheckboxSetContext } from '#UNSAFE_CheckboxSet';
import { useId } from '#hooks/UNSAFE_useId';
import { InlineUserAssistance, type UserAssistanceDensityType } from '#UNSAFE_UserAssistance';
import type { TestIdProps } from '#hooks/UNSAFE_useTestId';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import type { LayoutColumnSpan } from '#utils/UNSAFE_styles/Layout';
import { Size } from '#utils/UNSAFE_size';

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

// Typing should stay in sync with BaseRichSelection. They cannot be imported directly
// because BaseRichSelection is in a PRIVATE directory and will be removed in the production build.
type Props = TestIdProps &
  Pick<UnpackSignals<JSX.AriaAttributes>, 'aria-describedby'> & {
    /**
     * Text to provide guidance to help the user understand what data to enter.
     */
    assistiveText?: InlineUserAssistanceProps['assistiveText'];
    /**
     * Set of RichSelectionItems
     */
    children: ComponentChildren;
    /**
     * Specifies how many columns to span in a FormLayout with direction === 'row'
     */
    columnSpan?: LayoutColumnSpan;
    /**
     * Help source URL associated with the component.
     */
    helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];
    /**
     * Custom text to be rendered for the <code>helpSourceLink</code>.
     */
    helpSourceText?: InlineUserAssistanceProps['helpSourceText'];
    /**
     * Specifies if the set is disabled.
     */
    isDisabled?: boolean;
    /**
     * Specifies if the set is read-only.
     */
    isReadonly?: boolean;
    /**
     * Specifies whether the set requires a selection to be made.
     */
    isRequired?: boolean;
    /**
     * Label text for the set.
     */
    label: string;
    /**
     * Specifies where the label is positioned relative to the field.
     */
    labelEdge?: 'none' | 'top' | 'inside' | 'start';
    /**
     * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>.
     */
    labelStartWidth?: Size;
    /**
     * Specifies the layout of the cards and media
     */
    layout: 'xl' | 'md' | 'sm';
    /**
     * Messages to show on screen that are associated with the component.
     */
    messages?: InlineUserAssistanceProps['messages'];
    /**
     * Callback invoked each time the user changes selected option.
     */
    onCommit: (detail: ValueUpdateDetail<Set<string | number>>) => void;
    /**
     * Specifies the density of the user assistance presentation.  It can be set to:
     * <ul>
     * <li><code>'reflow'</code>: Show inline. Layout will reflow when text is displayed.</li>
     * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
     * assistance text is displayed.</li>
     * <li><code>'compact'</code>: Show inline and reserve space to prevent layout reflow when user
     * assistance text is displayed.</li>
     * </ul>
     */
    userAssistanceDensity?: UserAssistanceDensityType;
    /**
     * Value of the checkbox option.
     */
    value?: Set<string | number>;
  };

/**
 * A rich checkbox set allows the user to select options from a set of options.
 */
export const RichCheckboxSet = forwardRef(
  ({ children, onCommit, value, ...otherProps }: Props, ref?: Ref<FocusableHandle>) => {
    const baseId = useId();

    const checkboxSetContext = useMemo(
      () => ({
        name: baseId,
        value,
        onCommit
      }),
      [baseId, onCommit, value]
    );

    return (
      <CheckboxSetContext.Provider value={checkboxSetContext}>
        <BaseRichSelection
          {...otherProps}
          baseId={baseId}
          ref={ref}
          selection="multiple"
          value={value}>
          {children}
        </BaseRichSelection>
      </CheckboxSetContext.Provider>
    );
  }
);
